// FacilityCard — the standard "row" representation of a facility across
// the app. Pattern from 00-design-system.html §04 and 20-account-management.
//
// Anatomy (left to right):
//   • MarketStripe         — 3px vertical color accent for the market
//   • Body
//       row 1: RagDot + facility name + LifecyclePill (when not Active)
//       row 2: facility_id (mono) · type · address summary
//       row 3: SentimentPill + meta line ("3 open tasks · last walk 4d ago")
//   • Trailing chevron (when interactive)
//
// Designed to work standalone in a list and as a row inside a Card group.
// The whole card is clickable when an `href` or `onClick` is provided —
// renders as <a> via the Card primitive's `as` prop.

import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Card } from './Card';
import { RagDot } from './RagDot';
import { StatusPill } from './StatusPill';
import { MarketStripe } from './MarketStripe';
import {
  FacilityCleaningLines,
  type FacilityCleaningInfo,
} from './FacilityCleaningLines';
import { type FacilityLifecycle } from '../design-tokens';
import type { FeedbackSentiment, RagStatus } from '../types';

export interface FacilityCardProps {
  facilityName: string;
  facilityId?: string | null;
  facilityType?: string | null;
  /** City, ST or full address — caller decides how much to show. */
  addressSummary?: string | null;
  /**
   * 2026-05-29 — subtle hint shown when this card surfaced via an alias
   * match in the resolver (search_entities). Pass the `matched_on` string
   * (e.g. "OPR"); renders as a muted `matched “OPR”` note on the meta row.
   * Omit/null for name matches and the default unfiltered list.
   */
  aliasHint?: string | null;
  rag?: RagStatus | null;
  sentiment?: FeedbackSentiment | null;
  lifecycle?: FacilityLifecycle | null;
  /**
   * 2026-06-15 — when set (e.g. "Starts 7/1"), the facility hasn't started yet.
   * The operational RAG dot is suppressed (a pre-start facility shouldn't read
   * as "started/red") and this badge renders in its place. Presentation only.
   */
  futureStart?: string | null;
  /**
   * The canonical market name as stored, e.g. "Dallas-Fort Worth Metro".
   * 2026-09-10: the old `market` key prop is gone with the six-key vocabulary —
   * the stripe resolves the name against `public.markets` by exact match, so
   * there is no key to pass and nothing to keep in sync.
   */
  region?: string | null;
  /**
   * 2026-05-30 — "who's cleaning / who to call" lines. When provided, renders
   * the headline cleaner + partner/POC beneath the address row. Omit/undefined
   * keeps the card unchanged. Phone/email render as plain text here (the card
   * is already an <a>, so nested links would be invalid).
   */
  cleaning?: FacilityCleaningInfo | null;
  /** Trailing meta line, e.g. "3 open tasks · last walk 4d ago". */
  meta?: ReactNode;
  /** Right-aligned children rendered before the chevron. */
  trailing?: ReactNode;
  /** Wrap the card in a link. */
  href?: string;
  onClick?: () => void;
  /**
   * 2026-05-21 — when provided, the RagDot becomes click-to-edit.
   * Clicking it stops propagation (so it doesn't navigate via href) and
   * invokes this callback instead. Consumer is responsible for rendering
   * the popover. Card stays clickable elsewhere.
   */
  onRagClick?: () => void;
  className?: string;
}

export function FacilityCard({
  facilityName,
  facilityId,
  facilityType,
  addressSummary,
  aliasHint,
  rag,
  sentiment,
  lifecycle,
  futureStart,
  region,
  cleaning,
  meta,
  trailing,
  href,
  onClick,
  onRagClick,
  className,
}: FacilityCardProps) {
  const interactive = Boolean(href || onClick);
  const showLifecycle = lifecycle && lifecycle !== 'Active';
  // 2026-05-12 closed-facility rendering: when a facility is Closed the
  // RAG dot is meaningless (the account is gone) and the sentiment pill
  // is misleading (latest feedback predates closure). Hide both and let
  // the gray "Closed" lifecycle pill stand alone next to the name.
  const isClosed = lifecycle === 'Closed';
  const showRagDot = !isClosed;
  const showSentiment = !isClosed && sentiment;
  // 2026-06-15 — pre-start facility: suppress the OPERATIONAL dot only (Finance
  // is independent) and render the "Starts M/D" badge in its place.
  const isFutureStart = !isClosed && Boolean(futureStart);
  const showOperationalDot = showRagDot && !isFutureStart;

  const inner = (
    <div className="flex items-start gap-3 p-4">
      <MarketStripe region={region} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isFutureStart && (
                <span
                  title="Service hasn't started yet"
                  className="inline-flex shrink-0 items-center rounded-full border border-st-info-border bg-st-info-bg px-2 py-0.5 text-[11px] font-medium text-st-info-fg-strong"
                >
                  {futureStart}
                </span>
              )}
              {showOperationalDot && onRagClick ? (
                // Interactive variant: render the dot inside a button so it
                // captures clicks before the surrounding <a> navigates.
                <button
                  type="button"
                  aria-label={`Change Operational RAG (currently ${rag ?? 'unset'})`}
                  title={`Operational: ${rag ?? 'unset'}`}
                  className="-m-1 inline-flex shrink-0 items-center rounded-full p-1 hover:bg-tcs-bg-muted focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRagClick();
                  }}
                >
                  <RagDot status={rag ?? null} />
                </button>
              ) : (
                showOperationalDot && (
                  <span title={`Operational: ${rag ?? 'unset'}`}>
                    <RagDot status={rag ?? null} />
                  </span>
                )
              )}
              <h3 className="truncate text-[15px] font-semibold text-tcs-text">
                {facilityName}
              </h3>
              {showLifecycle && (
                <StatusPill variant="lifecycle" value={lifecycle!} />
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[12px] text-tcs-text-muted">
              {facilityId && (
                <span className="font-mono">{facilityId}</span>
              )}
              {facilityType && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>{facilityType}</span>
                </>
              )}
              {addressSummary && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="truncate">{addressSummary}</span>
                </>
              )}
              {aliasHint && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="italic text-tcs-text-muted">
                    matched &ldquo;{aliasHint}&rdquo;
                  </span>
                </>
              )}
            </div>
          </div>
          {showSentiment && (
            <StatusPill variant="sentiment" value={sentiment!} />
          )}
        </div>
        {cleaning && (
          <div className="mt-2">
            <FacilityCleaningLines info={cleaning} />
          </div>
        )}
        {meta && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-tcs-text-secondary">
            {meta}
          </div>
        )}
      </div>
      {trailing}
      {interactive && (
        <ChevronRight className="h-5 w-5 shrink-0 text-tcs-text-muted" />
      )}
    </div>
  );

  if (href) {
    return (
      <Card
        as="a"
        href={href}
        onClick={onClick}
        interactive
        className={`block ${className ?? ''}`}
      >
        {inner}
      </Card>
    );
  }
  if (onClick) {
    return (
      <Card
        as="button"
        type="button"
        onClick={onClick}
        interactive
        className={`block w-full text-left ${className ?? ''}`}
      >
        {inner}
      </Card>
    );
  }
  return <Card className={className}>{inner}</Card>;
}
